import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Typography,
  useMediaQuery,
  Theme,
  Modal,
  IconButton,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  fetchMembers,
  setSearchKey,
  setSelectedMembers
} from '../../../slices/NdisSlices/staffManagement/createStaffMembersSlice';
import { debounce } from 'lodash';
import { useAppSelector } from 'app/hooks';
import {
  addPeople,
  AddPeoplePayload,
  UserInfo,
  resetPeopleState
} from 'slices/payrolslices/PeopleSlice';
import { isFulfilled } from '@reduxjs/toolkit';

interface AddPeopleModalProps {
  open: boolean;
  onClose: () => void;
  onAdd?: (payload: AddPeoplePayload) => void;
}

interface AddPeopleResponse {
  message: string;
  summary: {
    total_processed: number;
    successful_inserts: number;
    skipped_employees: number;
    failed_employees: number;
  };
  details: {
    successful: Array<{ employee_id: string; email: string; path: string }>;
    skipped: Array<{ employee_id: string; email: string; reason: string }>;
    failed: Array<any>;
  };
}

const AddPeopleModal: React.FC<AddPeopleModalProps> = ({ open, onClose, onAdd }) => {
  const dispatch = useDispatch();
  const { members, loading, selectedMembers } = useSelector(
    (state: RootState) => state.createStaffMembers
  );
  const {
    loading: addingPeople,
    success,
    error,
    message
  } = useSelector((state: RootState) => state.people);

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [search, setSearch] = useState('');
  const [processingMembers, setProcessingMembers] = useState<string[]>([]);
  const [responseData, setResponseData] = useState<AddPeopleResponse | null>(null);

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  // Debug Redux state
  useEffect(() => {
    if (open && success) {
      console.log('Redux people state:', { success, message, error });
    }
  }, [open, success, message, error]);

  // Debounced search with project/service
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const payload = {
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          search_key: value || ''
        };
        dispatch(setSearchKey(value));
        dispatch(fetchMembers(payload));
      }, 500),
    [dispatch, selectedProjectId, selectedServiceTitle]
  );

  useEffect(() => {
    if (open && !responseData) {
      debouncedSearch('');
      setSearch('');
      dispatch(setSelectedMembers([]));
      setProcessingMembers([]);
      dispatch(resetPeopleState());
    }
    return () => debouncedSearch.cancel();
  }, [open, debouncedSearch, dispatch, responseData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleToggle = useCallback(
    (id: string) => {
      dispatch(
        setSelectedMembers(
          selectedMembers.includes(id)
            ? selectedMembers.filter((v) => v !== id)
            : [...selectedMembers, id]
        )
      );
    },
    [dispatch, selectedMembers]
  );

  const handleAddSelected = async () => {
    if (selectedMembers.length === 0) return;

    setProcessingMembers([...selectedMembers]);

    const userInfoArray: UserInfo[] = selectedMembers
      .map((memberId) => {
        const employee = members.find((m: any) => m.id === memberId);
        return {
          id: employee?.id || '',
          name: employee?.name || '',
          email: employee?.email || ''
        };
      })
      .filter(Boolean);

    const payload: AddPeoplePayload = {
      group_id: selectedProjectId || '',
      service_id: selectedServiceTitle || '',
      user_info: userInfoArray
    };

    try {
      const action = await dispatch(addPeople(payload));
      if (isFulfilled(action)) {
        console.log('addPeople response:', action.payload); // Debug log
        setResponseData(action.payload as AddPeopleResponse);
        if (onAdd) {
          onAdd(payload);
        }
        dispatch(setSelectedMembers([]));
      }
    } catch (err) {
      console.error('Failed to add selected members:', err);
    } finally {
      setProcessingMembers([]);
    }
  };

  const handleBackToAddPeople = () => {
    setResponseData(null);
    setSearch('');
    dispatch(setSelectedMembers([]));
    dispatch(resetPeopleState());
  };

  const renderResponseDetails = () => {
    if (!responseData) return null;

    const { message, summary, details } = responseData;

    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="success" sx={{ mb: 2, fontSize: isSmallScreen ? '0.75rem' : '0.875rem' }}>
          {message}
        </Alert>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: isSmallScreen ? '0.75rem' : '0.875rem', mb: 1 }}
        >
          Summary
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" sx={{ fontSize: isSmallScreen ? '0.6875rem' : '0.75rem' }}>
            Total Processed: {summary.total_processed}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: isSmallScreen ? '0.6875rem' : '0.75rem' }}>
            Successful: {summary.successful_inserts}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: isSmallScreen ? '0.6875rem' : '0.75rem' }}>
            Skipped: {summary.skipped_employees}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: isSmallScreen ? '0.6875rem' : '0.75rem' }}>
            Failed: {summary.failed_employees}
          </Typography>
        </Box>

        {details.successful.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: isSmallScreen ? '0.75rem' : '0.875rem', mb: 1 }}
            >
              Successfully Added
            </Typography>
            <List dense sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {details.successful.map((emp) => (
                <ListItem key={emp.employee_id} sx={{ py: 0.5 }}>
                  <ListItemIcon>
                    <CheckCircleIcon
                      color="success"
                      sx={{ fontSize: isSmallScreen ? '1rem' : '1.25rem' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={emp.email}
                    primaryTypographyProps={{
                      fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}

        {details.skipped.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: isSmallScreen ? '0.75rem' : '0.875rem', mb: 1 }}
            >
              Skipped Employees
            </Typography>
            <List dense sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {details.skipped.map((emp) => (
                <ListItem key={emp.employee_id} sx={{ py: 0.5 }}>
                  <ListItemIcon>
                    <WarningIcon
                      color="warning"
                      sx={{ fontSize: isSmallScreen ? '1rem' : '1.25rem' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={emp.email}
                    secondary={emp.reason}
                    primaryTypographyProps={{
                      fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
                    }}
                    secondaryTypographyProps={{
                      fontSize: isSmallScreen ? '0.6875rem' : '0.75rem'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}

        {details.successful.length === 0 && details.skipped.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: isSmallScreen ? '0.6875rem' : '0.75rem', mt: 2 }}
          >
            No employees were added or skipped.
          </Typography>
        )}

        <Button
          variant="text"
          color="primary"
          onClick={handleBackToAddPeople}
          sx={{
            mt: 2,
            fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Back to Add People
        </Button>
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: 400 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: isSmallScreen ? 1 : 2,
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant={isSmallScreen ? 'subtitle2' : 'subtitle1'}
            fontWeight="bold"
            sx={{ fontSize: isSmallScreen ? '0.75rem' : '0.875rem' }}
          >
            Add people for screening
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: isSmallScreen ? '1.125rem' : '1.25rem' }} />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: isSmallScreen ? '0.75rem' : '0.875rem' }}>
            {error}
          </Alert>
        )}

        {responseData ? (
          renderResponseDetails()
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email"
              value={search}
              onChange={handleSearchChange}
              sx={{
                mb: 2,
                '& .MuiInputBase-input': { fontSize: isSmallScreen ? '0.75rem' : '0.875rem' }
              }}
            />

            {loading ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                {members.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                    No employees found
                  </Typography>
                ) : (
                  members.map((employee: any) => (
                    <ListItem
                      key={employee.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5
                      }}
                      disableGutters
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Checkbox
                          checked={selectedMembers.includes(employee.id)}
                          onChange={() => handleToggle(employee.id)}
                          size="small"
                          sx={{ mr: 1, p: 0.5 }}
                        />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {employee.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: isSmallScreen ? '0.6875rem' : '0.75rem',
                              color: 'text.secondary'
                            }}
                          >
                            {employee.email}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  ))
                )}
              </List>
            )}

            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={selectedMembers.length === 0 || loading || processingMembers.length > 0}
              onClick={handleAddSelected}
              sx={{
                mt: 2,
                fontSize: isSmallScreen ? '0.6875rem' : '0.75rem',
                py: isSmallScreen ? 0.5 : 1,
                px: isSmallScreen ? 1 : 1.5,
                minWidth: 'auto',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none'
              }}
              fullWidth
            >
              {processingMembers.length > 0 ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Add Selected ({selectedMembers.length})
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default AddPeopleModal;
